// Records site visits to the backend for the admin analytics dashboard.
// - Generates a stable session_id (localStorage, 30-min sliding window).
// - Fires one visit per (session, path) per 30 minutes to keep counts sane.
// - Silently no-ops on failure — analytics must never break the app.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/App";
import { getAuthToken } from "@/lib/firebase";

const SESSION_KEY = "pouls_visit_session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes
const RECENT_HITS_KEY = "pouls_visit_recent"; // { [path]: timestamp }

const getOrCreateSessionId = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const now = Date.now();
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.id && now - parsed.last < SESSION_TTL_MS) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id: parsed.id, last: now }));
        return parsed.id;
      }
    }
    const id = `s_${Math.random().toString(36).slice(2, 10)}${now.toString(36)}`;
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id, last: now }));
    return id;
  } catch {
    return `s_ephemeral_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const shouldRecordPath = (path) => {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(RECENT_HITS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const last = map[path];
    if (last && now - last < SESSION_TTL_MS) return false;
    map[path] = now;
    // Prune the map to avoid unbounded growth
    const trimmed = Object.entries(map)
      .filter(([, ts]) => now - ts < SESSION_TTL_MS * 4)
      .slice(-40);
    localStorage.setItem(RECENT_HITS_KEY, JSON.stringify(Object.fromEntries(trimmed)));
    return true;
  } catch {
    return true;
  }
};

export function useVisitTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname || "/";
    if (!shouldRecordPath(path)) return;

    const send = async () => {
      try {
        const session_id = getOrCreateSessionId();
        const headers = { "Content-Type": "application/json" };
        // Attach auth if we have it, so admin visits can be excluded later if needed
        try {
          const token = await getAuthToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch {
          // ignore — anonymous visit is fine
        }
        await fetch(`${API}/analytics/visit`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            session_id,
            path,
            referrer: document.referrer || null,
          }),
          keepalive: true,
        });
      } catch {
        // Silent — analytics must never disrupt UX
      }
    };
    send();
  }, [location.pathname]);
}
